import $ from "jquery";
import _ from "underscore";
import async from "async";
import Operation from "core/models/Operation";
import OperationSet from "core/models/OperationSet";
import ObjectLayerSelector from "app/views/map/ObjectLayerSelector";
import LayerEditor from "app/views/layer/LayerEditor";
import LayerUploadView from "app/views/layer/LayerUploadView";
import auth from "app/auth";
import ErrorUtils from "app/utils/ErrorUtils";
import session from "app/Session";
import clipboard from "core/Clipboard";
import modals from "core/views/modals";
import urlService from "app/UrlService";
import mapContext from "app/views/map/MapContext";

export default new OperationSet([
  new Operation({
    id: "delete-layer",
    text: "Delete Layer",
    icon: "trash",
    action: function (model) {
      var deferred = new $.Deferred();

      ObjectLayerSelector.destroyLayer(
        model.featureLayer,
        "Layer",
        function (err) {
          if (err) {
            if (err !== true) {
              deferred.reject(err);
            } else {
              deferred.reject(false);
            }
          } else {
            model.serviceLayer.removeLayer(model);
            deferred.resolve(model, "Layer deleted successfully.");
          }
        }
      );

      return deferred.promise();
    },
    available: function (model) {
      return !!(
        model &&
        model.serviceLayer &&
        !model.serviceLayer.service.get("external") &&
        model.featureLayer.allows(session, "delete")
      );
    },
  }),

  new Operation({
    id: "move-layer",
    text: "Move Layer",
    icon: "move",
    action: function (model, options) {
      var deferred = new $.Deferred();

      var layer = mapContext
        .getConfiguration()
        .moveLayer(
          options.oldParent.data.service,
          options.oldParent.data.layer,
          options.oldIndex,
          options.newParent.data.service,
          options.newParent.data.layer,
          options.newIndex
        );

      var siblings;

      if (options.newParent.data.layer) {
        siblings = options.newParent.data.layer.children;
      } else {
        siblings = options.newParent.data.service.layers;
      }

      var collection;

      if (options.newParent.data.layer) {
        collection = options.newParent.data.layer.featureLayer.collection;
      } else {
        collection = options.newParent.data.service.featureLayers; //todo fix this - shouldn't be using the "featureLayers" property - why? - because it makes testing difficult
      }

      async.series(
        [
          function (cb) {
            if (siblings) {
              async.each(
                siblings,
                function (sibling, cb) {
                  sibling.featureLayer.collection = collection;
                  sibling.featureLayer.save(null, {
                    success: function () {
                      cb();
                    },
                    error: function (model, response) {
                      cb(response);
                    },
                  });
                },
                function (err) {
                  if (err) {
                    return cb(err);
                  }

                  cb();
                }
              );
            } else {
              cb();
            }
          },
          function (cb) {
            if (layer) {
              layer.featureLayer.collection = collection;
              layer.featureLayer.save(null, {
                success: function () {
                  cb();
                },
                error: function (model, response) {
                  cb(response);
                },
              });
            } else {
              cb();
            }
          },
        ],
        function (err) {
          if (err) {
            return deferred.reject(err);
          } else {
            return deferred.resolve(model, "Layer moved successfully");
          }
        }
      );

      return deferred.promise();
    },
    available: function () {
      return true; //todo check for permissions
    },
  }),

  new Operation({
    id: "manage-layer-security",
    text: "Manage Security",
    icon: "lock",
    action: function (model) {
      return mapContext.getOperation("manage-security").action(model, {
        type: {
          DataType: model.featureLayer,
        },
        actions: {
          read: "View objects and properties of the layer.",
          delete: "Delete the layer.",
          update: "Update properties of the layer, including security.",
          add: "Add objects to the layer.",
          remove: "Remove objects from the layer.",
          modify: "Modify objects on the layer.",
        },
      });
    },
    available: function (model) {
      return !!(
        model &&
        model.serviceLayer &&
        !model.serviceLayer.service.get("external") &&
        model.featureLayer.allows(session, "update")
      );
    },
  }),

  new Operation({
    id: "edit-layer",
    text: "Edit Layer",
    icon: "pencil",
    action: function (model) {
      var deferred = new $.Deferred();
      var featureLayer = model.featureLayer;
      var layerEditor = new LayerEditor({
        model: featureLayer,
      });

      var oldFields = featureLayer.getFields().models.slice(); //get a snapshot of the existing fields in case we need to restore

      layerEditor.render();
      modals.confirm(
        "Edit Fields",
        layerEditor.$el,
        {
          type: "primary",
          text: "Save",
        },
        function () {
          layerEditor
            .save()
            .done(function () {
              layerEditor.remove(); //ensure event handlers are unsubscribed
              deferred.resolve(model, "Layer updated successfully.");
            })
            .fail(function (model, response) {
              layerEditor.remove();
              featureLayer.getFields().reset(oldFields); //reset to old values
              deferred.reject(response);
            });
        },
        "Cancel",
        function () {
          _.delay(function () {
            layerEditor.remove();
            featureLayer.getFields().reset(oldFields); //reset to old values
            deferred.reject(false);
          }, 250);
        }
      );

      return deferred.promise();
    },
    available: function (model) {
      return !!(
        model &&
        model.serviceLayer &&
        !model.serviceLayer.service.get("external") &&
        model.featureLayer.allows(session, "update")
      );
    },
  }),

  new Operation({
    id: "save-layer",
    text: "Save Layer",
    icon: "floppy-disk",
    action: function (model) {
      var deferred = new $.Deferred();

      model.featureLayer.save(null, {
        success: function () {
          deferred.resolve(model, "Layer updated successfully.");
        },
        error: function (model, response) {
          deferred.reject(response);
        },
      });

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        model.serviceLayer &&
        !model.serviceLayer.service.get("external") &&
        model.featureLayer.allows(session, "update")
      );
    },
  }),

  new Operation({
    id: "select-layer",
    text: "Select",
    action: function (model) {
      mapContext.getConfiguration().setActiveLayer(model);
      mapContext.getConfiguration().setActiveService(model.serviceLayer);

      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "layer-set-active",
    text: "Set Active",
    action: function (model) {
      mapContext.getConfiguration().getDrawingLayer().deactivateDraw();
      mapContext.getConfiguration().setDrawingLayer(model);

      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "layer-select-all",
    text: "Select All Records",
    action: function (model) {
      var deferred = new $.Deferred();
      var layer = model;
      var models = model.getGeometries();

      _.each(models, function (model) {
        model.originalLayer = layer;
      });

      mapContext.clearSelection();
      mapContext.setSelection(models);
      mapContext
        .getOperation("highlight-geometry")
        .action(models)
        .then(function () {
          return mapContext
            .getOperation("show-geometry-details")
            .action(models);
        })
        .then(
          function () {
            deferred.resolve(model);
          },
          function (err) {
            deferred.reject(err, model);
          }
        );

      return deferred.promise();
    },
    available: function (model) {
      return (
        model &&
        (!model.serviceLayer || !model.serviceLayer.service.get("external"))
      );
    },
  }),

  new Operation({
    id: "layer-print-all",
    text: "Print Records",
    icon: "print",
    action: function (model) {
      var deferred = new $.Deferred();
      var layer = model;

      var findRecords = function (cb) {
        var models = layer.getGeometries();

        _.each(models, function (model) {
          model.originalLayer = layer;
        });

        cb(null, models);
      };

      if (model.serviceLayer && model.serviceLayer.service.get("external")) {
        findRecords = function (cb) {
          require(["app/models/map/MapQuery"], function (MapQuery) {
            var query = new MapQuery.default(model);

            query
              .where("1 = 1")
              .reference(mapContext.getMap().spatialReference)
              .execute()
              .done(function (records) {
                cb(null, records);
              })
              .fail(function (err) {
                cb(err);
              });
          });
        };
      }

      findRecords(function (err, records) {
        if (err) {
          deferred.reject(err);
        } else {
          mapContext
            .getOperation("print-geometry")
            .action(records)
            .done(function () {
              deferred.resolve(model);
            })
            .fail(function (err) {
              deferred.reject(err);
            });
        }
      });

      return deferred.promise();
    },
  }),

  new Operation({
    id: "layer-tabulate-all",
    text: "View All Records",
    action: function (model) {
      var layer = model;
      var models = model.getGeometries();
      var msg = null;

      _.each(models, function (model) {
        model.originalLayer = layer;
      });

      if (mapContext.getOperation("export-geometry-table").available(models)) {
        mapContext
          .getOperation("export-geometry-table")
          .action(models)
          .fail(
            ErrorUtils.errorHandler({
              activity: "tabulating the geometry",
            })
          );
      } else {
        msg = "No records found.";
      }

      return Operation.resolve(model, msg);
    },
    available: function (model) {
      return (
        model &&
        (!model.serviceLayer || !model.serviceLayer.service.get("external"))
      );
    },
  }),

  new Operation({
    id: "layer-bulk-add",
    icon: "cloud-upload",
    text: "Bulk Add",
    action: function (model) {
      var featureLayer = model.featureLayer;
      var deferred = new $.Deferred();
      var uploadView = new LayerUploadView({
        url:
          urlService.apiurl("/layers/" + featureLayer.id + "/upload") +
          "?token=" +
          auth.token,
      });

      uploadView.render();

      var content = uploadView.$el;

      var modalView = modals.confirm(
        "Bulk Add",
        content,
        "Upload",
        function () {
          uploadView.save().done(function () {
            featureLayer.fetch({
              success: function () {
                model
                  .reload()
                  .done(function () {
                    deferred.resolve(model, "File data added successfully.");
                  })
                  .fail(function (err) {
                    deferred.reject(err);
                  });
              },
              error: function (model, response) {
                deferred.reject(response);
              },
            });

            modalView.remove();
          });
        },
        "Cancel",
        function () {
          deferred.reject(false);
        }
      );

      return deferred.promise();
    },
    available: function (model) {
      return (
        model.serviceLayer &&
        !model.serviceLayer.service.get("external") &&
        model.featureLayer
      );
    },
  }),

  new Operation({
    id: "copy-layer",
    icon: "copy",
    text: "Copy",
    action: function (model) {
      var featureLayer = model.featureLayer;
      var fields = featureLayer.getFields();
      var layerData = featureLayer.toJSON();

      delete layerData.id;
      delete layerData.fieldSet;
      delete layerData.updatedAt;
      delete layerData.createdAt;
      delete layerData.owner;
      layerData.name += " (Copy)";

      var data = {
        layerData: layerData,
        fieldData: fields.map(function (field) {
          var fieldData = field.toJSON();

          delete fieldData.id;
          delete fieldData.fieldSet;
          delete fieldData.updatedAt;
          delete fieldData.createdAt;
          delete fieldData.owner;

          return fieldData;
        }),
      };

      clipboard.put("layer-structure", data);

      return Operation.resolve(model);
    },
    available: function (model) {
      return model && model.featureLayer;
    },
  }),

  new Operation({
    id: "cancel-drawing",
    icon: "",
    text: "Cancel Drawing",
    action: function (model) {
      return Operation.resolve(model);
    },
  }),

  new Operation({
    id: "commit-drawing",
    icon: "",
    text: "Commit Drawing",
    action: function (model) {
      return Operation.resolve(model);
    },
  }),
]);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/operations/map/LayerOperations.js
// module id = 902
// module chunks = 1